import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { optionalTrue, searchPatch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useCan, useCanWithoutBuilding } from "@/lib/policy";
import { useCanChangeHrEntry } from "./-components/hr-entry-access";
import type { HrTemplate, TemplateFormValues } from "./-components/catalog";
import { TemplateDetail } from "./-components/template-detail";
import { TemplateForm } from "./-components/template-form";
import { TemplateList } from "./-components/template-list";

export const Route = createFileRoute("/_authenticated/_workforce/hr/templates")({
  validateSearch: (search: Record<string, unknown>): { retired?: true } => ({
    retired: optionalTrue(search.retired),
  }),
  component: TemplatesPage,
});

/** Named sets of employee documents, so a new hire in a given role can be asked for the whole
 * bundle at once instead of one document at a time. */
function TemplatesPage() {
  const { facilityId } = useFacility();
  // Templates are the organization's, kept by their own id with no building named.
  const canManage = useCanWithoutBuilding("hr:manage");
  const canSeeFacilities = useCan("facility:read");
  // Each template changes with hr:manage wherever it applies; a new one goes where HR is managed.
  const canChange = useCanChangeHrEntry();
  const place = useNewRowPlace("hr:manage");
  const hrFacilities = [...place.buildings];
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<"create" | "edit" | null>(null);
  const [retiring, setRetiring] = useState<HrTemplate | null>(null);
  const { retired: showRetiredParam } = Route.useSearch();
  const showRetired = showRetiredParam === true;
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const setShowRetired = (next: boolean) => patch({ retired: next ? true : undefined });

  const templates = useQuery(
    orpc.workforce.employeeDocuments.listTemplates.queryOptions({
      input: { includeInactive: showRetired },
    }),
  );
  const documentTypes = useQuery(
    orpc.workforce.employeeDocuments.listTypes.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
    enabled: canSeeFacilities,
  });
  const detail = useQuery({
    ...orpc.workforce.employeeDocuments.getTemplate.queryOptions({
      input: { templateId: selectedId ?? "" },
    }),
    enabled: selectedId !== null,
  });

  const facilityOptions = facilities.data ?? [];
  const typeOptions = documentTypes.data ?? [];
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.employeeDocuments.key() });

  const save = useMutation(
    orpc.workforce.employeeDocuments.saveTemplate.mutationOptions({
      onSuccess: async (saved) => {
        toast.success(editing === "edit" ? "Template updated" : "Template created");
        setEditing(null);
        setSelectedId(saved.id);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const duplicate = useMutation(
    orpc.workforce.employeeDocuments.duplicateTemplate.mutationOptions({
      onSuccess: async (copy) => {
        toast.success("Template duplicated");
        setSelectedId(copy.id);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const restore = useMutation(
    orpc.workforce.employeeDocuments.restoreTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template back in use");
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const retire = useMutation(
    orpc.workforce.employeeDocuments.deactivateTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template retired");
        setSelectedId(null);
        setRetiring(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const submit = (values: TemplateFormValues) =>
    save.mutate({
      ...values,
      templateId: editing === "edit" ? (selectedId ?? undefined) : undefined,
      description: values.description?.trim() ? values.description : null,
      items: values.items.map((item, index) => ({ ...item, sortOrder: index })),
    });

  return (
    <>
      <PageHeader
        title="Document templates"
        count={templates.data?.length}
        meta="A named bundle of documents, so a new hire is asked for the whole set at once."
        actions={
          <>
            <Button variant="outline" onClick={() => setShowRetired(!showRetired)}>
              {showRetired ? "Hide retired" : "Show retired"}
            </Button>
            {canManage ? <Button onClick={() => setEditing("create")}>New template</Button> : null}
          </>
        }
      />

      {templates.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : templates.isError ? (
        <ReadFailed
          message="Could not load templates."
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : templates.data.length === 0 ? (
        <EmptyState
          title={showRetired ? "No templates, retired or in use" : "No templates yet"}
          description="Group the document types a role has to hold into a template."
        />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <TemplateList
            rows={templates.data}
            facilities={facilityOptions}
            selectedId={selectedId}
            canManage={canManage}
            canChange={canChange}
            restoring={restore.isPending}
            onSelect={(template) => setSelectedId(template.id)}
            onRestore={(template) => restore.mutate({ templateId: template.id })}
          />
          {selectedId === null ? (
            <EmptyState
              title="Nothing selected"
              description="Choose a template to see the documents it asks for."
            />
          ) : detail.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : detail.isError ? (
            <ReadFailed
              message="Could not load this template."
              error={detail.error}
              onRetry={() => void detail.refetch()}
              retrying={detail.isFetching}
            />
          ) : (
            <TemplateDetail
              template={detail.data}
              documentTypes={typeOptions}
              facilities={facilityOptions}
              canManage={canManage && canChange(detail.data)}
              duplicating={duplicate.isPending}
              onEdit={() => setEditing("edit")}
              onDuplicate={() => duplicate.mutate({ templateId: detail.data.id })}
              onRetire={() =>
                setRetiring(
                  templates.data.find((template) => template.id === detail.data.id) ?? null,
                )
              }
            />
          )}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing === "edit" ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription>
              Choose the documents this set asks for and which facilities may use it.
            </DialogDescription>
          </DialogHeader>
          {editing === "edit" && detail.data ? (
            <TemplateForm
              key={detail.data.id}
              documentTypes={typeOptions}
              facilities={hrFacilities}
              allAllowed={place.everyBuilding}
              pending={save.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={submit}
              defaultValues={{
                name: detail.data.name,
                description: detail.data.description ?? "",
                appliesToAllFacilities: detail.data.appliesToAllFacilities,
                facilityIds: detail.data.facilityIds,
                items: detail.data.items.map((item) => ({
                  documentTypeId: item.documentTypeId,
                  name: item.name,
                  description: item.description,
                  isRequired: item.isRequiredOverride ?? item.isRequired,
                })),
              }}
            />
          ) : null}
          {editing === "create" ? (
            <TemplateForm
              key="new"
              documentTypes={typeOptions}
              facilities={hrFacilities}
              allAllowed={place.everyBuilding}
              pending={save.isPending}
              submitLabel="Create"
              onCancel={() => setEditing(null)}
              onSubmit={submit}
              defaultValues={{
                appliesToAllFacilities: place.startAt === null && place.everyBuilding,
                facilityIds: place.startAt ? [place.startAt] : [],
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={retiring !== null}
        onOpenChange={(open) => (open ? null : setRetiring(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire &ldquo;{retiring?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              It can no longer be assigned. Requirements already given to employees from it are
              kept, and &ldquo;Show retired&rdquo; puts it back in use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep template</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => retiring && retire.mutate({ templateId: retiring.id })}
            >
              Retire template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
